import React, { useState, useEffect, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { 
  FiSearch, 
  FiMessageSquare, 
  FiVideo, 
  FiX, 
  FiCheckCircle, 
  FiClock, 
  FiFeather, 
  FiSend, 
  FiBookOpen,
  FiTag,
  FiCopy,
  FiCheck,
  FiPhone,
  FiMail,
  FiGift
} from 'react-icons/fi'
import toast from 'react-hot-toast'
import { apiConnector } from '../../../../services/apiConnector'
import { lookupLearnerById } from '../../../../services/operations/couponAPI'

export function MyLearners({ setActiveSection, telemetryData, onUpdate }) {
  const { token } = useSelector((state) => state.auth)
  const [searchTerm, setSearchTerm] = useState('')
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [clientEmail, setClientEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [pendingApprovals, setPendingApprovals] = useState([])
  const [approvingId, setApprovingId] = useState(null)
  const [copiedId, setCopiedId] = useState(null)

  // Quick Learner ID Lookup State
  const [lookupIdInput, setLookupIdInput] = useState('')
  const [lookupLoading, setLookupLoading] = useState(false)
  const [lookedUpLearner, setLookedUpLearner] = useState(null)

  const handleCopyId = (id) => {
    if (!id) return
    navigator.clipboard.writeText(id)
    setCopiedId(id)
    toast.success('Learner ID copied!')
    setTimeout(() => setCopiedId(null), 2000)
  }

  const handleLookupLearner = async (e) => {
    if (e) e.preventDefault()
    if (!lookupIdInput.trim()) {
      return toast.error('Please enter a Learner ID (e.g. LRN-102938)')
    }
    setLookupLoading(true)
    const res = await lookupLearnerById(lookupIdInput.trim(), token)
    setLookupLoading(false)
    if (res?.success && res?.learner) {
      setLookedUpLearner(res.learner)
      toast.success(`Found learner: ${res.learner.name}`)
    } else {
      toast.error(res?.message || 'No learner found with that ID.')
      setLookedUpLearner(null)
    }
  }

  // Reflections state
  const [reflectionModalLearner, setReflectionModalLearner] = useState(null)
  const [reflectionsList, setReflectionsList] = useState([])
  const [newPromptText, setNewPromptText] = useState('')
  const [sendingPrompt, setSendingPrompt] = useState(false)

  const clients = telemetryData?.clients || []

  // Load connected clients and pending connection payment approvals
  const loadApprovals = useCallback(async () => {
    if (!token) return
    try {
      const res = await apiConnector(
        'GET',
        '/api/v1/practitioners/connected-clients',
        null,
        { Authorization: `Bearer ${token}` }
      )
      if (res?.data?.success) {
        setPendingApprovals(res.data.pendingApprovals || [])
      }
    } catch (err) {
      console.error('Error fetching pending approvals:', err)
    }
  }, [token])

  // Load reflections assigned/answered
  const loadReflections = useCallback(async () => {
    if (!token) return
    try {
      const res = await apiConnector(
        'GET',
        '/api/v1/reflections/practitioner',
        null,
        { Authorization: `Bearer ${token}` }
      )
      if (res?.data?.success) {
        setReflectionsList(res.data.prompts || [])
      }
    } catch (err) {
      console.error('Error fetching practitioner reflections:', err)
    }
  }, [token])

  useEffect(() => {
    loadApprovals()
    loadReflections()
  }, [loadApprovals, loadReflections])

  // Handle Practitioner approving or rejecting a client payment connection request
  const handleApproveConnection = async (connectionId, status = 'approved') => {
    if (!token) return
    setApprovingId(connectionId)
    try {
      const res = await apiConnector(
        'POST',
        '/api/v1/practitioners/approve-connection',
        { connectionId, status },
        { Authorization: `Bearer ${token}` }
      )

      if (res?.data?.success) {
        toast.success(res.data.message || `Client connection ${status}!`)
        loadApprovals()
        if (onUpdate) onUpdate()
      } else {
        toast.error(res?.data?.message || 'Action failed.')
      }
    } catch (err) {
      console.error('Approve connection error:', err)
      toast.error('Could not update connection approval status.')
    } finally {
      setApprovingId(null)
    }
  }

  const handleAddClient = async (e) => {
    e.preventDefault()
    if (!clientEmail) {
      toast.error('Please enter client email address.')
      return
    }

    setSubmitting(true)
    try {
      const res = await apiConnector(
        'POST',
        '/api/v1/practitioners/connect',
        { practitionerId: telemetryData?.practitioner?.id, email: clientEmail },
        { Authorization: `Bearer ${token}` }
      )
      if (res?.data?.success) {
        toast.success(res.data.message || 'Client added/connected successfully!')
        setShowInviteModal(false)
        setClientEmail('')
        if (onUpdate) onUpdate()
      } else {
        toast.error(res?.data?.message || 'Could not add client.')
      }
    } catch (err) {
      console.error('Add client error:', err)
      toast.error('Could not connect client.')
    } finally {
      setSubmitting(false)
    }
  }

  const handleSendReflectionPrompt = async (e) => {
    e.preventDefault()
    if (!newPromptText.trim() || !reflectionModalLearner) return
    setSendingPrompt(true)
    try {
      const res = await apiConnector(
        'POST',
        '/api/v1/reflections/create',
        { clientId: reflectionModalLearner._id, promptText: newPromptText },
        { Authorization: `Bearer ${token}` }
      )
      if (res?.data?.success) {
        toast.success('Reflection prompt sent to learner!')
        setNewPromptText('')
        loadReflections()
      } else {
        toast.error(res?.data?.message || 'Failed to send prompt.')
      }
    } catch (err) {
      console.error('Send reflection error:', err)
      toast.error('Could not send reflection prompt.')
    } finally {
      setSendingPrompt(false)
    }
  }

  const filteredClients = clients.filter((c) => {
    const fullName = `${c.firstName || ''} ${c.lastName || ''}`.toLowerCase()
    const email = (c.email || '').toLowerCase()
    const lId = (c.learnerId || '').toLowerCase()
    const phone = (c.contactNumber || c.additionalDetails?.contactNumber || '').toLowerCase()
    const query = searchTerm.toLowerCase()
    return fullName.includes(query) || email.includes(query) || lId.includes(query) || phone.includes(query)
  })

  // Filter reflections for currently selected learner in modal
  const learnerReflections = reflectionModalLearner
    ? reflectionsList.filter((r) => r.client?._id === reflectionModalLearner._id || r.client === reflectionModalLearner._id)
    : []

  return (
    <section className="view on" id="clients">
      {/* Header */}
      <div className="htop" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div className="crumb">My learners &amp; Connection Requests</div>
          <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#0F172A' }}>{clients.length} Active Learner(s) &amp; Students</h1>
        </div>
      </div>

      {/* QUICK LEARNER ID LOOKUP CARD */}
      <div style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #312E81 100%)', borderRadius: '16px', padding: '22px 24px', marginBottom: '24px', color: '#FFF', boxShadow: '0 10px 25px -5px rgba(30, 27, 75, 0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '14px', marginBottom: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '17px', fontWeight: 800, color: '#FFFFFF' }}>
              <FiTag style={{ color: '#38BDF8' }} /> Learner ID Directory Lookup
            </div>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#C7D2FE' }}>
              Enter any learner's unique Learner ID to immediately view their complete profile (Name, Email, Phone Number, Photo) and grant discounts.
            </p>
          </div>
        </div>

        <form onSubmit={handleLookupLearner} style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', maxWidth: '600px' }}>
          <div style={{ position: 'relative', flex: '1 1 280px' }}>
            <FiTag style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
            <input
              type="text"
              placeholder="Enter Learner ID (e.g. LRN-102938)..."
              value={lookupIdInput}
              onChange={(e) => setLookupIdInput(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 36px',
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                background: 'rgba(15, 23, 42, 0.6)',
                color: '#FFF',
                fontSize: '13px',
                fontWeight: 600,
                outline: 'none',
                fontFamily: 'monospace',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <button
            type="submit"
            disabled={lookupLoading}
            style={{
              background: '#38BDF8',
              color: '#0F172A',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '10px',
              fontWeight: 800,
              fontSize: '13px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              transition: 'all 0.2s',
            }}
          >
            {lookupLoading ? 'Searching...' : <><FiSearch size={14} /> Search Learner</>}
          </button>

          {lookedUpLearner && (
            <button
              type="button"
              onClick={() => {
                setLookedUpLearner(null)
                setLookupIdInput('')
              }}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                color: '#E2E8F0',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                padding: '10px 14px',
                borderRadius: '10px',
                fontWeight: 700,
                fontSize: '13px',
                cursor: 'pointer',
              }}
            >
              Clear
            </button>
          )}
        </form>

        {/* LOOKED UP LEARNER FULL PROFILE CARD */}
        {lookedUpLearner && (
          <div
            style={{
              marginTop: '18px',
              background: '#FFFFFF',
              borderRadius: '14px',
              padding: '18px 20px',
              color: '#0F172A',
              border: '2px solid #38BDF8',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '16px',
              boxShadow: '0 10px 20px rgba(0,0,0,0.15)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #4F46E5, #7C3AED)',
                  color: '#FFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: '18px',
                  overflow: 'hidden',
                  flexShrink: 0,
                  border: '2px solid #E0E7FF',
                }}
              >
                {lookedUpLearner.image && !lookedUpLearner.image.includes('dicebear') ? (
                  <img
                    src={lookedUpLearner.image}
                    alt={lookedUpLearner.name}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                      const fallback = e.currentTarget.nextElementSibling
                      if (fallback) fallback.style.display = 'flex'
                    }}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : null}
                <span
                  style={{
                    display: lookedUpLearner.image && !lookedUpLearner.image.includes('dicebear') ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                    lineHeight: 1,
                  }}
                >
                  {(lookedUpLearner.firstName?.trim()?.slice(0, 1) || 'L').toUpperCase()}
                </span>
              </div>

              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: '#0F172A' }}>
                    {lookedUpLearner.name}
                  </h3>
                  <span
                    style={{
                      background: '#EFF6FF',
                      color: '#1D4ED8',
                      border: '1px solid #BFDBFE',
                      padding: '2px 8px',
                      borderRadius: '6px',
                      fontSize: '11px',
                      fontWeight: 800,
                      fontFamily: 'monospace',
                    }}
                  >
                    ID: {lookedUpLearner.learnerId}
                  </span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '6px', flexWrap: 'wrap', fontSize: '13px', color: '#475569' }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <FiMail style={{ color: '#6366F1' }} /> {lookedUpLearner.email}
                  </span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <FiPhone style={{ color: '#10B981' }} /> {lookedUpLearner.contactNumber || 'No Phone Number'}
                  </span>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => {
                  if (setActiveSection) setActiveSection('coupons')
                }}
                style={{
                  background: '#4F46E5',
                  color: '#FFF',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '8px 16px',
                  fontWeight: 700,
                  fontSize: '13px',
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <FiGift size={14} /> Give Discount / Free Access
              </button>
            </div>
          </div>
        )}
      </div>

      {/* PENDING PAYMENT APPROVALS SECTION */}
      {pendingApprovals.length > 0 && (
        <div style={{ background: '#EFF6FF', border: '1px solid #93C5FD', borderRadius: '16px', padding: '20px', marginBottom: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#1E40AF', fontWeight: 800, fontSize: '16px', marginBottom: '12px' }}>
            <FiClock size={20} /> Pending Learner Connections ({pendingApprovals.length})
          </div>
          <p style={{ color: '#1E3A8A', fontSize: '13px', marginBottom: '16px' }}>
            The following learners have booked your session or offer via the directory. Confirm connection to start managing sessions.
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {pendingApprovals.map((pa) => {
              const cl = pa.client || {}
              const cName = `${cl.firstName || 'Learner'} ${cl.lastName || ''}`
              return (
                <div
                  key={pa._id}
                  style={{
                    background: '#FFFFFF',
                    border: '1px solid #FDE68A',
                    borderRadius: '12px',
                    padding: '14px 18px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '12px',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                        color: '#FFF',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {cName.slice(0, 2).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 800, color: '#0F172A' }}>{cName}</h4>
                        {cl.learnerId && (
                          <span style={{ fontSize: '11px', background: '#EEF2FF', color: '#4338CA', border: '1px solid #C7D2FE', padding: '1px 6px', borderRadius: '4px', fontWeight: 700, fontFamily: 'monospace' }}>
                            {cl.learnerId}
                          </span>
                        )}
                      </div>
                      <p style={{ margin: 0, fontSize: '12px', color: '#64748B' }}>
                        {cl.email} • {cl.contactNumber || cl.additionalDetails?.contactNumber || 'No Phone'} • Paid: <b style={{ color: '#166534' }}>₹{pa.amountPaid || 2500}</b> • Ref: {pa.paymentId || 'PayGlocal Verified'}
                      </p>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleApproveConnection(pa._id, 'approved')}
                      disabled={approvingId === pa._id}
                      style={{
                        background: '#166534',
                        color: '#FFF',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '13px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                      }}
                    >
                      <FiCheckCircle size={15} /> Approve Learner
                    </button>
                    <button
                      onClick={() => handleApproveConnection(pa._id, 'rejected')}
                      disabled={approvingId === pa._id}
                      style={{
                        background: '#FEF2F2',
                        color: '#991B1B',
                        border: '1px solid #FCA5A5',
                        padding: '8px 14px',
                        borderRadius: '8px',
                        fontWeight: 700,
                        fontSize: '13px',
                        cursor: 'pointer',
                      }}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Main Connected Learners Table */}
      <div className="card" style={{ padding: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#0F172A' }}>Learners Booked With You</h3>
          <div style={{ position: 'relative', width: '280px' }}>
            <FiSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94A3B8' }} />
            <input
              type="text"
              placeholder="Search by name, email, or Learner ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px 8px 36px',
                borderRadius: '8px',
                border: '1px solid #CBD5E1',
                fontSize: '13px',
                outline: 'none',
              }}
            />
          </div>
        </div>

        <div className="scroll" style={{ overflowX: 'auto' }}>
          <table className="tbl" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #E2E8F0', background: '#F8FAFC' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#64748B', fontSize: '12.5px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Learner</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#64748B', fontSize: '12.5px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Learner ID</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#64748B', fontSize: '12.5px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email / Phone</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#64748B', fontSize: '12.5px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Enrolled Date</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#64748B', fontSize: '12.5px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Enroll For</th>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#64748B', fontSize: '12.5px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Amount Paid</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.length > 0 ? (
                filteredClients.map((client) => {
                  return (
                    <tr key={client._id} style={{ borderBottom: '1px solid #F1F5F9' }}>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div
                            style={{
                              width: '36px',
                              height: '36px',
                              borderRadius: '50%',
                              background: '#2563EB',
                              color: '#FFF',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              fontSize: '13px',
                            }}
                          >
                            {client.image && !client.image.includes('dicebear') ? (
                              <img
                                src={client.image}
                                alt=""
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none'
                                  const fallback = e.currentTarget.nextElementSibling
                                  if (fallback) fallback.style.display = 'flex'
                                }}
                                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                              />
                            ) : null}
                            <span
                              style={{
                                display: client.image && !client.image.includes('dicebear') ? 'none' : 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                height: '100%',
                                lineHeight: 1,
                              }}
                            >
                              {(client.firstName?.trim()?.slice(0, 1) || 'L').toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <b style={{ color: '#0F172A' }}>{client.firstName} {client.lastName}</b>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        {client.learnerId ? (
                          <button
                            onClick={() => handleCopyId(client.learnerId)}
                            title="Click to copy Learner ID"
                            style={{
                              background: '#EEF2FF',
                              color: '#4338CA',
                              border: '1px solid #C7D2FE',
                              padding: '4px 8px',
                              borderRadius: '6px',
                              fontWeight: 700,
                              fontFamily: 'monospace',
                              fontSize: '12px',
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '5px',
                            }}
                          >
                            <FiTag size={12} />
                            {client.learnerId}
                            {copiedId === client.learnerId ? <FiCheck size={12} color="#16A34A" /> : <FiCopy size={11} style={{ opacity: 0.7 }} />}
                          </button>
                        ) : (
                          <span style={{ color: '#94A3B8', fontSize: '12px' }}>LRN-PENDING</span>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: '13px' }}>
                        <div style={{ color: '#0F172A', fontWeight: 500 }}>{client.email}</div>
                        {(client.contactNumber || client.additionalDetails?.contactNumber) && (
                          <div style={{ color: '#64748B', fontSize: '12px', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <FiPhone size={11} style={{ color: '#10B981' }} /> {client.contactNumber || client.additionalDetails?.contactNumber}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '14px 16px', color: '#64748B', fontSize: '13px' }}>
                        {client.createdAt ? new Date(client.createdAt).toLocaleDateString() : 'Active Member'}
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ fontWeight: 600, color: '#475569' }}>
                          {client.enrolledFor || '1:1 Session'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ marginBottom: '8px' }}>
                          <span style={{ fontWeight: 700, color: '#166534', background: '#DCFCE7', padding: '4px 8px', borderRadius: '6px' }}>
                            ₹{client.amountPaid || '2,500'}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button
                            className="mini"
                            style={{ background: '#FAF5FF', color: '#7C3AED', border: '1px solid #E9D5FF', borderRadius: '8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px', fontSize: '11px', fontWeight: 600 }}
                            onClick={() => setReflectionModalLearner(client)}
                            title="Assign and view reflection prompts"
                          >
                            <FiFeather size={11} /> Reflections
                          </button>
                          <button
                            className="mini"
                            style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', borderRadius: '8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px', fontSize: '11px', fontWeight: 600 }}
                            onClick={() => setActiveSection('community')}
                            title="Open direct chat in Community Hub"
                          >
                            <FiMessageSquare size={11} /> Message
                          </button>
                          <button
                            className="mini"
                            style={{ background: '#2563EB', color: '#FFF', border: 'none', borderRadius: '8px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 8px', fontSize: '11px', fontWeight: 600 }}
                            onClick={() => setActiveSection('room')}
                            title="Schedule live session"
                          >
                            <FiVideo size={11} /> Live
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={6} style={{ textAlign: 'center', padding: '32px', color: '#64748B' }}>
                    {searchTerm ? `No learners matching "${searchTerm}".` : 'No connected learners found.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* LEARNER REFLECTIONS MODAL */}
      {reflectionModalLearner && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100,
          }}
        >
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              width: '90%',
              maxWidth: '620px',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '24px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #7C3AED, #2563EB)', color: '#FFF', display: 'flex', alignItems: 'center', justify: 'center', fontWeight: 800 }}>
                  <FiFeather size={18} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0F172A' }}>
                    Reflections for {reflectionModalLearner.firstName} {reflectionModalLearner.lastName}
                  </h3>
                  <p style={{ margin: 0, fontSize: '12px', color: '#64748B' }}>{reflectionModalLearner.email}</p>
                </div>
              </div>
              <button
                onClick={() => setReflectionModalLearner(null)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', fontSize: '20px' }}
              >
                <FiX />
              </button>
            </div>

            {/* Form to Assign New Reflection Prompt */}
            <form onSubmit={handleSendReflectionPrompt} style={{ background: '#FAF5FF', border: '1px solid #E9D5FF', borderRadius: '14px', padding: '16px', marginBottom: '20px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#6B21A8', marginBottom: '6px' }}>
                ✍️ Send a New Reflection Journal Prompt
              </label>
              <textarea
                rows={3}
                placeholder="e.g. What is one pattern or intention you want to explore before our next session?"
                value={newPromptText}
                onChange={(e) => setNewPromptText(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '10px',
                  border: '1px solid #D8B4FE',
                  fontSize: '13.5px',
                  outline: 'none',
                  marginBottom: '10px',
                  background: '#FFF',
                }}
                required
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="submit"
                  disabled={sendingPrompt}
                  style={{
                    background: 'linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)',
                    color: '#FFF',
                    border: 'none',
                    padding: '8px 18px',
                    borderRadius: '10px',
                    fontWeight: 700,
                    fontSize: '13px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                >
                  <FiSend size={14} /> {sendingPrompt ? 'Sending...' : 'Send Prompt to Learner'}
                </button>
              </div>
            </form>

            {/* Reflection Journal History */}
            <div>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 800, color: '#0F172A', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <FiBookOpen size={16} className="text-purple-600" /> Reflection History &amp; Responses
              </h4>

              {learnerReflections.length === 0 ? (
                <div style={{ background: '#F8FAFC', borderRadius: '12px', padding: '24px', textAlign: 'center', color: '#64748B', fontSize: '13.5px' }}>
                  No reflection prompts assigned to this learner yet. Use the form above to send their first prompt!
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {learnerReflections.map((r) => (
                    <div
                      key={r._id}
                      style={{
                        background: '#FFFFFF',
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px',
                        padding: '14px 16px',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontWeight: 700, color: '#1E293B', fontSize: '14px' }}>"{r.promptText}"</span>
                        <span
                          style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '3px 8px',
                            borderRadius: '6px',
                            background: r.status === 'answered' ? '#DCFCE7' : r.status === 'skipped' ? '#F1F5F9' : '#FEF3C7',
                            color: r.status === 'answered' ? '#166534' : r.status === 'skipped' ? '#64748B' : '#92400E',
                          }}
                        >
                          {r.status === 'answered' ? (r.isPrivate ? 'Private Answer' : 'Shared Answer') : r.status === 'skipped' ? 'Skipped' : 'Pending Learner'}
                        </span>
                      </div>

                      {r.status === 'answered' ? (
                        <div style={{ background: '#F8FAFC', borderLeft: '3px solid #7C3AED', padding: '10px 12px', borderRadius: '6px', fontSize: '13px', color: '#334155' }}>
                          <b>Learner's Reflection:</b> {r.answerText}
                          <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '4px' }}>
                            Answered on {r.answeredAt ? new Date(r.answeredAt).toLocaleDateString() : 'Recently'}
                          </div>
                        </div>
                      ) : r.status === 'skipped' ? (
                        <div style={{ fontSize: '12px', color: '#94A3B8', fontStyle: 'italic' }}>
                          Learner opted to skip this prompt.
                        </div>
                      ) : (
                        <div style={{ fontSize: '12px', color: '#D97706', fontStyle: 'italic' }}>
                          Waiting for learner to write their reflection...
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99,
          }}
        >
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              width: '90%',
              maxWidth: '460px',
              padding: '24px',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0F172A' }}>
                Connect Learner
              </h3>
              <button
                onClick={() => setShowInviteModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748B', fontSize: '20px' }}
              >
                <FiX />
              </button>
            </div>

            <form onSubmit={handleAddClient}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: 700, color: '#334155', marginBottom: '6px' }}>
                  Learner Email Address
                </label>
                <input
                  type="email"
                  placeholder="e.g. learner@example.com"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 14px',
                    borderRadius: '10px',
                    border: '1px solid #CBD5E1',
                    fontSize: '14px',
                    outline: 'none',
                  }}
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '10px',
                    background: '#F1F5F9',
                    border: 'none',
                    color: '#475569',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    padding: '10px 20px',
                    borderRadius: '10px',
                    background: 'linear-gradient(135deg, #1F5FE0 0%, #8A2BE0 100%)',
                    border: 'none',
                    color: '#FFF',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {submitting ? 'Connecting...' : 'Connect Learner'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  )
}

export default MyLearners
